import * as React from 'react';
import { CSSTransitionGroup } from 'react-transition-group';
import { inject, observer } from 'mobx-react';
import { Route, RouteComponentProps, Switch } from 'react-router';
import { ShareDialog } from '../../components/Common/ShareDialog';
import { PremiumVideoPopup } from '../../components/Premium/PremiumVideoPopup';
import { VideoShow } from '../../components/Video/VideoShow';
import {
  STORE_ADVERT,
  STORE_AUTH,
  STORE_SUBSCRIPTION,
  STORE_VIDEO,
  AdvertSlot
} from '../../constants';
import { Video } from '../../models';
import {
  AdvertStore,
  AuthStore,
  SubscriptionStore,
  VideoStore
} from '../../stores';
import './style.css';
import * as ReactGA from 'react-ga';

export enum VideoType {
  Video = 'videos',
  Featured = 'featured'
}

export interface VideoPageParams {
  videoType: VideoType;
  slug: string;
  url: string;
}

export interface VideoPageProps extends RouteComponentProps<VideoPageParams> {
  /* [STORE_VIDEO]: VideoStore; */
}

export interface VideoPageState {
  // TODO: Remove this if unused now...?
  /* HACK: Using state to track data loading */
  /* FIXME: Make mobx updates work (adverts not showing on internal nav to featured vids) */
  dataLoaded?: boolean;
}

@inject(STORE_ADVERT)
@inject(STORE_AUTH)
@inject(STORE_SUBSCRIPTION)
@inject(STORE_VIDEO)
@observer
export class VideoPage extends React.Component<VideoPageProps, VideoPageState> {
  constructor(props: VideoPageProps) {
    super(props);
    this.state = {};
  }

  // Initial render
  async componentDidMount() {
    // TODO: Parse id from slug and use that when we have ids on models
    const { slug, videoType } = this.getRouteParams();

    this.loadVideoPageData(videoType, slug);
  }

  // Prop updates (e.g. when navigating from video to video)
  async UNSAFE_componentWillReceiveProps(nextProps: VideoPageProps) {
    // TODO: Parse id from slug and use that when we have ids on models
    const { slug, videoType } = this.getRouteParams(nextProps);

    this.loadVideoPageData(videoType, slug);
  }

  componentWillUnmount() {
    // Noop our async data set method to prevent errors for instantly/quickly
    // unmounted video page
    this.safeSetState = () => undefined;
  }

  render() {
    const { videoType, url } = this.getRouteParams();
    const advertStore = this.props[STORE_ADVERT] as AdvertStore;
    const authStore = this.props[STORE_AUTH] as AuthStore;
    const subscriptionStore = this.props[
      STORE_SUBSCRIPTION
    ] as SubscriptionStore;
    const videoStore = this.props[STORE_VIDEO] as VideoStore;

    const video = this.getVideoForRoute();

    const { featuredLoading, videoForRouteLoading } = videoStore;

    const videoError =
      videoType === VideoType.Featured
        ? featuredLoading.error
        : videoForRouteLoading.error;

    const videoLoading =
      !(video || videoError) ||
      (videoType === VideoType.Featured
        ? featuredLoading.running
        : videoForRouteLoading.running);

    const getVideoUrl =
      videoType === VideoType.Featured
        ? Video.getFeaturedRoute
        : Video.getRoute;

    const { next, previous } =
      videoType === VideoType.Featured
        ? VideoStore.getNextAndPreviousFeaturedVideos(
            videoStore.featuredVideos,
            video
          )
        : { next: undefined, previous: undefined };

    return (
      <>
        {subscriptionStore.videoPopupShowing ? (
          <PremiumVideoPopup
            onClose={() => subscriptionStore.closeVideoPopup()}
            onRequestSignUp={() => {
              subscriptionStore.closeVideoPopup();
              subscriptionStore.requestSignUp();
            }}
          />
        ) : null}
        <VideoShow
          advertSlotOne={advertStore.adverts[AdvertSlot.VideoPageOne]}
          advertSlotTwo={advertStore.adverts[AdvertSlot.VideoPageTwo]}
          advertSlotMobileOne={
            advertStore.adverts[AdvertSlot.VideoPageMobileOne]
          }
          advertSlotMobileTwo={
            advertStore.adverts[AdvertSlot.VideoPageMobileTwo]
          }
          currentUser={authStore.currentUser}
          getVideoUrl={getVideoUrl}
          loggedIn={!!authStore.currentUser}
          nextVideo={next}
          onFavourite={() => video && this.onFavourite(video)}
          onFirstPlay={() => {
            if (video) {
              this.onPlay(video.src);
            }
            return video && videoStore.saveVideoEvent(video, 'play', '00:00');
          }}
          onPremiumPlaybackBlocked={() => {
            this.onPremiumPlaybackBlocked(video ? video.src : '');
          }}
          pageUrl={url}
          previousVideo={previous}
          savingFavourite={!!videoStore.favouritingVideo.running}
          video={video}
          videoError={!!videoError}
          videoLoading={videoLoading}
        >
          <CSSTransitionGroup
            className="share-dialog-wrapper"
            transitionName="show-share-dialog"
            transitionEnterTimeout={200}
            transitionLeaveTimeout={200}
          >
            <Switch key={this.props.location.key}>
              <Route
                path={`${url}/share`}
                exact={true}
                children={({ match }) =>
                  match ? (
                    <ShareDialog
                      url={url}
                      title={video && video.name ? video.name : 'Edge of Play'}
                      onClose={this.closeShareDialog}
                    >
                      <p>
                        Click on one of the buttons below to be taken to the
                        desired application to share this video, or copy the URL
                        link:
                      </p>
                    </ShareDialog>
                  ) : null
                }
              />
            </Switch>
          </CSSTransitionGroup>
        </VideoShow>
      </>
    );
  }

  private getRouteParams = (
    props: VideoPageProps = this.props
  ): VideoPageParams => {
    const {
      match: {
        params: { slug, videoType },
        url
      }
    } = props;

    return {
      slug,
      url,
      videoType
    };
  };

  private loadVideoPageData = async (videoType: VideoType, route: string) => {
    this.setState({ dataLoaded: false });
    const videoStore = this.props[STORE_VIDEO] as VideoStore;

    if (videoType === VideoType.Featured) {
      await videoStore.fetchFeaturedVideos();
    }

    const videoId = Video.getIdFromRelativeRoute(route);

    if (typeof videoId === 'number' && !isNaN(videoId)) {
      // Load the video if it's not in the store already
      await videoStore.fetchVideoForRoute(videoId);
    }

    // TODO: Failed to parse video id from slug - error?

    // Video loaded in store, now fetch adverts and related videos
    await Promise.all([this.loadVideoAdvertData(), this.fetchRelatedVideos()]);

    this.safeSetState({ dataLoaded: true });
  };

  private loadVideoAdvertData = async () => {
    const advertStore = this.props[STORE_ADVERT] as AdvertStore;

    const video = this.getVideoForRoute();

    if (!video) {
      return;
    }

    const { rootCategory } = video;

    await Promise.all(
      [
        AdvertSlot.VideoPageOne,
        AdvertSlot.VideoPageTwo,
        AdvertSlot.VideoPageMobileOne,
        AdvertSlot.VideoPageMobileTwo
      ].map(slot => advertStore.fetchAdvertsForCategorySlot(slot, rootCategory))
    );
  };

  private closeShareDialog = () => {
    // We close the share dialog be re-navigating to the base url for this page
    this.props.history.push(this.props.match.url);
  };

  private safeSetState = (state: Partial<VideoPageState>) => {
    this.setState(state);
  };

  private onFavourite = (video: Video): void => {
    const videoStore = this.props[STORE_VIDEO] as VideoStore;

    videoStore.favouriteVideo(video);
  };

  private getVideoForRoute = (): Video | undefined => {
    const { slug } = this.getRouteParams();
    const videoStore = this.props[STORE_VIDEO] as VideoStore;

    const { videos } = videoStore;

    const id = Video.getIdFromRelativeRoute(slug);

    return (videos || []).find(_video => _video.id === id);
  };

  private fetchRelatedVideos = async (): Promise<void> => {
    const videoStore = this.props[STORE_VIDEO] as VideoStore;
    const video = this.getVideoForRoute();

    if (video && video.category) {
      await videoStore.fetchVideosForCategory(video.category);
    }
  };

  private onPlay = (videoSrc: string | undefined): void => {
    ReactGA.event({
      category: 'Videos',
      action: 'Clicked to play video.',
      label: videoSrc
    });
  };

  private onPremiumPlaybackBlocked = (videoSrc: string | undefined) => {
    const subscriptionStore = this.props[
      STORE_SUBSCRIPTION
    ] as SubscriptionStore;

    ReactGA.event({
      category: 'Videos',
      action: 'Clicked to play premium video.',
      label: videoSrc
    });

    subscriptionStore.showVideoPopup();
  };
}

export default VideoPage;



// WEBPACK FOOTER //
// ./src/app/containers/VideoPage/index.tsx